import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import '../App.css';
import ProfilePopup from './ProfilePopup';
import ReferenceDocs from './ReferenceDocs';
import MessageActions from './MessageActions';
import Sidebar from './Sidebar';
import { API_ENDPOINTS } from './config/apiConfig';
import { useAuth } from '../context/AuthContext';
import SettingsScreen from './SettingsScreen';
import { toast } from 'react-toastify';
import LoadingSpinner from './LoadingSpinner';
//import { Console } from 'console';

// --- TIPOS ---
interface ChatScreenProps { onLogout: () => void; }
interface ChatMessage { role: 'user' | 'assistant'; content: string; docs?: Record<string, string>; isActionable?: boolean;}
interface HistoryItem { id: string; title: string; }
interface ApiTurn { datetime: string; question: string; answer: string; docs: string | Record<string, string>; }

// --- COMPONENTE PRINCIPAL ---
const ChatScreen: React.FC<ChatScreenProps> = ({ onLogout }) => {
  const { authData } = useAuth();
  const [currentView, setCurrentView] = useState<'chat' | 'settings'>('chat');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [isProfilePopupVisible, setProfilePopupVisible] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [historyList, setHistoryList] = useState<HistoryItem[]>([]);
  const [fullHistoryData, setFullHistoryData] = useState<Record<string, ApiTurn[]>>({});
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const messageAreaRef = useRef<HTMLDivElement>(null);

    // --- EFECTOS ---
  useEffect(() => {
    const baseTitle = process.env.REACT_APP_WEBSITE_TITLE || "Chat";
    document.title = baseTitle;
    if (authData.username) {
      fetchHistoryFromApi(authData.username);
    } else {
      onLogout();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData.username]); // Se ejecuta cuando el username del contexto está disponible

  useEffect(() => {
    if (messageAreaRef.current) {
      messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // --- FUNCIONES ---
  const fetchHistoryFromApi = async (user: string) => {
    setIsHistoryLoading(true);
    const token = authData.token; 
    if (!token) { onLogout(); return; }
    try {
      const response = await fetch(API_ENDPOINTS.getChatHistory(user), { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Error al obtener historial');
      const data = await response.json();
      const historyFromApi = data.history || {};
      const initialList: HistoryItem[] = Object.keys(historyFromApi)
        .map(chatId => ({ id: chatId, title: historyFromApi[chatId][0].question }))
        .sort((a,b) => new Date(historyFromApi[b.id][0].datetime).getTime() - new Date(historyFromApi[a.id][0].datetime).getTime());
      setHistoryList(initialList);
      setFullHistoryData(historyFromApi);
      setActiveChatId(null);
      setMessages([{ 
        role: 'assistant', 
        content: `<h2>Bienvenido, ${user}.</h2><p>Selecciona una conversación del historial o crea un nuevo chat para comenzar.</p>` 
      }]);
    } catch (error) {
        setActiveChatId(null);
        setMessages([{
            role: 'assistant',
            content: `<h2>Bienvenido, ${user}.</h2><p>No se pudo cargar tu historial. Puedes crear un nuevo chat.</p>`
        }]);
    } finally {
        setIsHistoryLoading(false);
    }
  };

  const handleSelectChat = (chatId: string) => {
    setActiveChatId(chatId);
    const conversation = fullHistoryData[chatId];
    if (!conversation) {
      setMessages([{role: 'assistant', content: 'Error: No se pudo cargar la conversación.'}]);
      return;
    }
    const newMessages: ChatMessage[] = [];
    conversation.forEach(turn => {
      newMessages.push({ role: 'user', content: turn.question });
      newMessages.push({ 
        role: 'assistant', 
        content: turn.answer, 
        docs: parseDocsFromString(turn.docs),
        isActionable: true // <-- LÍNEA CLAVE AÑADIDA
      });
    });
    setMessages(newMessages);
    if (isSidebarVisible) setSidebarVisible(false);
  };
  
  const handleNewChat = (currentUser: string) => {
    const newChatId = uuidv4();
    setActiveChatId(newChatId);
    setMessages([{ role: 'assistant', content: `¡Hola, ${currentUser}! ¿Listo para una nueva consulta?` }]);
    if (isSidebarVisible) setSidebarVisible(false);
  };

const parseDocsFromString = (docsData: string | Record<string, string>): Record<string, string> => {
    if (typeof docsData === 'object' && docsData !== null) return docsData;
    if (typeof docsData !== 'string' || !docsData.trim()) return {};
    const docsObject: Record<string, string> = {};
    let entries: string[] = [];
    if (docsData.startsWith("{'") && docsData.endsWith("'}") ) {
      const innerContent = docsData.slice(2, -2);
      entries = innerContent.split("', '");
    } else {
      entries = docsData.split(/\n---\n/);
    }
    entries.forEach(entry => {
      const cleanedEntry = entry.trim().replace(/"/g, '').replace(/\\\\/g, '\\');
      if (cleanedEntry) docsObject[cleanedEntry] = '#';
    });
    return docsObject;
  };

  const handleSendMessage = async () => {
    if (!currentMessage.trim() || isLoading || !activeChatId || !authData.username || !authData.token) {
      if (!authData.token) onLogout(); return;
    }
    let currentChatId = activeChatId;
    if (!currentChatId) {
        currentChatId = uuidv4();
        setActiveChatId(currentChatId);
        setMessages([]); 
    }
    //console.log("Enviando currentChatId: ", currentChatId, "  activeChatId: ", activeChatId);

    if (!currentMessage.trim() || isLoading || !currentChatId || !authData.username || !authData.token) {
      if (!authData.token) onLogout();
      return;
    }
    const userMessage: ChatMessage = { role: 'user', content: currentMessage };
    const conversationHistory = activeChatId === currentChatId ? messages : [];
    setMessages(prev => [...prev, userMessage]);
    const questionToSend = currentMessage;
    setCurrentMessage('');
    setIsLoading(true);
    try {
      const responseCount = await fetch(API_ENDPOINTS.getCountChats(authData.username), { headers: { 'Authorization': `Bearer ${authData.token}` } });
      if (!responseCount.ok) throw new Error('No se pudo verificar el límite de consultas.');
      const dataCount = await responseCount.json();
      
      const today = new Date();
      const todayStats = dataCount.statistics.find((stat: any) => 
        stat._id.year === today.getFullYear() && 
        stat._id.month === today.getMonth() + 1 && 
        stat._id.day === today.getDate()
      );
      
      const consultaCount = todayStats ? todayStats.count : 0;
      const limiteConsultas = (authData.role === "admin" || (authData.daysRemaining || 0) > 0) ? Infinity : 4;

      if (consultaCount >= limiteConsultas) {
        toast.warn(`Has alcanzado el límite de ${limiteConsultas} consultas por hoy.`);
        setIsLoading(false);
        // Revertimos el mensaje del usuario si se alcanza el límite
        setMessages(prev => prev.slice(0, -1));
        return;
      }

      // Procede con el envío de la pregunta
      const apiHistory = conversationHistory
        .filter(msg => msg.role === 'assistant' && !(msg.content.includes('Lamento informarte que la pregunta que has planteado') || msg.content.includes('¿Listo para una nueva consulta?') || msg.content.includes('Error'))) 
        .map(msg => ({
            role: msg.role,
            content: msg.content
        }));
      const pythonFriendlyDate = new Date().toISOString().slice(0, -1);
      //console.log("Enviando apiHistory:", apiHistory);
      const requestBody = { username: authData.username, chat_id: currentChatId, datetime: pythonFriendlyDate, 
        query: questionToSend, history: apiHistory, };
      //console.log("Enviando requestBody:", requestBody);
      const response = await fetch(API_ENDPOINTS.chat, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
          body: JSON.stringify(requestBody),
        });
      
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Ocurrió un error al procesar la pregunta.');
        
        const parsedDocs = parseDocsFromString(data.docs);
        const assistantMessage: ChatMessage = { role: 'assistant', content: data.answer, docs: parsedDocs, isActionable: true, };
        setMessages(prev => [...prev, assistantMessage]);
        
        const chatExistsInHistory = historyList.some(item => item.id === currentChatId);
        if (!chatExistsInHistory) {
          const newHistoryItem: HistoryItem = { id: currentChatId, title: questionToSend };
          setHistoryList(prev => [newHistoryItem, ...prev]);
        }
        
        const newTurn: ApiTurn = { datetime: requestBody.datetime, question: questionToSend, answer: data.answer, docs: data.docs, };
        setFullHistoryData(prev => ({ ...prev, [activeChatId]: [...(prev[activeChatId] || []), newTurn] }));
        
      
    } catch (error: any) {
      const errorMessage: ChatMessage = { role: 'assistant', content: `<div style="color: #ff8a80; padding: 10px;"><strong>Error:</strong><p>${error.message}</p></div>` };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRateResponse = async (rating: 'good' | 'bad', question: string, messageIndex: number): Promise<boolean> => {
    if (!authData.token || !authData.username || !activeChatId) { alert("No se puede calificar: Sesión o chat no activo."); return false; }
    try {
      const isoDateString = new Date().toISOString();
      const pythonFriendlyDate = isoDateString.slice(0, -1);
      const requestBody = { username: authData.username, chat_id: activeChatId, message_index: messageIndex, rating, datetime_obj: pythonFriendlyDate, question };
      const response = await fetch(API_ENDPOINTS.rateResponse, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.detail || "Error al enviar la calificación."); }
      return true;
    } catch (error: any) {
      alert(`Error: ${error.message}`);
      return false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } };
  const toggleSidebar = () => setSidebarVisible(prev => !prev);

  // --- RENDERIZADO CONDICIONAL ---
  if (currentView === 'settings') {
    return <SettingsScreen onBackToChat={() => setCurrentView('chat')} />;
  }
  // --- RENDERIZADO ---
  return (
    <>
      <div className="chat-container">
        {/* El overlay y el popup se manejan fuera o con un portal si es necesario, 
            pero la lógica clave es la clase en la sidebar */}
        {isSidebarVisible && <div className="overlay visible" onClick={toggleSidebar}></div>}
        <Sidebar
          // --- CORRECCIÓN CLAVE ---
          // Pasamos la clase dinámicamente al componente Sidebar
          className={`sidebar ${isSidebarVisible ? 'visible' : ''}`}
          isHistoryLoading={isHistoryLoading}
          historyList={historyList}
          username={authData.username || '...'}
          onNewChat={() => handleNewChat(authData.username || 'usuario')}
          onSelectChat={handleSelectChat}
          onProfileClick={() => setProfilePopupVisible(!isProfilePopupVisible)}
        />
        <div className="main-content">
          {/* Aquí podrías mostrar el rol o los días restantes */}
          {/* <div style={{position: 'absolute', top: 10, right: 10, color: 'gray'}}>Rol: {authData.role} | Días: {authData.daysRemaining}</div> */}
          <div className="hamburger-menu" onClick={toggleSidebar}>☰</div>

          <div className="message-area" ref={messageAreaRef}>
             {messages.map((msg, index) => (
                <div key={index} className={`message ${msg.role}`}>
                  {msg.role === 'user' ? ( msg.content ) : (
                    <>
                      <div dangerouslySetInnerHTML={{ __html: msg.content }} />
                      {msg.docs && Object.keys(msg.docs).length > 0 && <ReferenceDocs docs={msg.docs} />}
                      {msg.isActionable && (
                        <MessageActions 
                          answerContent={msg.content}
                          onRate={(rating) => {
                            const questionIndex = Math.floor((index - 1) / 2);
                            const questionText = messages[index - 1]?.content || "";
                            return handleRateResponse(rating, questionText, questionIndex);
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              ))}
              {isLoading && <div className="loading-spinner-button">
                  <LoadingSpinner />
                </div>}
          </div>

          <div className="input-area">
             <textarea
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={activeChatId ? "Escribe tu mensaje aquí..." : "Crea un nuevo chat para comenzar"}
                rows={1}
                disabled={isLoading || !activeChatId}
              />

              {/* --- RENDERIZADO CONDICIONAL DEL BOTÓN --- */}
              {isLoading ? (
                // Si está cargando, muestra el spinner animado
                <div className="loading-spinner-button">
                  <LoadingSpinner />
                </div>
              ) : (
                // Si no, muestra el botón de enviar
                <button 
                  onClick={handleSendMessage} 
                  disabled={!currentMessage.trim() || !activeChatId}
                  title="Enviar"
                >
                  ➤
                </button>
              )}
          </div>
        </div>
      </div>
      {isProfilePopupVisible && (
        <ProfilePopup 
          onClose={() => setProfilePopupVisible(false)}
          onLogout={onLogout}
          onGoToSettings={() => setCurrentView('settings')}
        />
      )}
    </>
  );
};

export default ChatScreen;